import DocumentViewerModal from "components/DocumentViewer";
import EditDoesntCharge from "components/EditDoesntCharge";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import ProsecutorFeedback from "pages/InvestigationFileDetail/fragment/ProsecutorFeedback";
import ProsecutorFeedbackView from "pages/InvestigationFileDetail/fragment/ProsecutorFeedbackView";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  editCrimeReferences,
  fetchCrimeReferencesByCRNumberAndIncidentRegNo,
} from "services/incidentService";
import {
  addCoordinatorComment,
  editReceivingInvestigationFile3,
} from "services/prosecutorPoliceService";
import { CrimeReference, FILE_URL, InvestigationFile, Roles } from "types";
import { editIcon } from "utils/constant";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  file: InvestigationFile;
  fetchData: () => void;
};

const DoesntChargeTab: React.FC<Props> = ({ file, fetchData }) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [incident, setIncident] = useState<CrimeReference>();
  const [showReInitiateConfirm, setShowReInitiateConfirm] = useState(false);
  const [reInitiateLoading, setReInitiateLoading] = useState(false);
  const [reInitiateReason, setReInitiateReason] = useState("");
  const [showPreviousDecisionFiles, setShowPreviousDecisionFiles] =
    useState(false);

  // Prosecutor decision files (newest first). Uploaded `decision_file`s are
  // stored as DMS documents with document_type "prosecutor_decision".
  const decisionFiles = (file?.documents || [])
    .filter((d) => d?.document_type === "prosecutor_decision")
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.created_at || "").getTime() -
        new Date(a?.created_at || "").getTime(),
    );
  const latestDecisionFile = decisionFiles[0];
  const previousDecisionFiles = decisionFiles.slice(1);
  const isCoordinator = hasRole(loginData?.roles, [
    Roles.coordinator,
    Roles.corruptionCoordinator,
    Roles.economicCoordinator,
    Roles.variousCoordinator,
  ]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);

  useEffect(() => {
    if (file) {
      fetchCrimeReferencesByCRNumberAndIncidentRegNo(
        file?.crime_reference_number ?? "",
        file?.incident_registration_number ?? "",
      ).then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setIncident(data[0]);
        }
      });
    }
  }, [file]);

  const handleApprove = async () => {
    try {
      setLoadingApprove(true);
      // Plain JSON PATCH (no file) — sends status reliably.
      editReceivingInvestigationFile3({
        id: file.id,
        prosecutor_decision_status: "approved",
        // Approving a not-to-charge decision closes the case at the
        // prosecutor stage.
        status: "closed_by_prosecutor",
      })
        .then(() => {
          showTooltip(t("successfully_submitted"), "success");
          fetchData();
        })
        .catch((err) => {
          showTooltip(errorHandlerOnForm(err?.response?.data), "error");
        })
        .finally(() => {
          setLoadingApprove(false);
        });
    } catch (err) {
      console.error(err);
      alert("Approve failed.");
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleReInitiate = async () => {
    if (!reInitiateReason.trim()) {
      showTooltip(
        t("re_initiation_reason_required", {
          defaultValue: "Re-initiation reason is required.",
        }),
        "error",
      );
      return;
    }
    setReInitiateLoading(true);
    try {
      if (!incident) return;

      // Re-initiating re-opens the closed case: send it back into the
      // workflow, clear the prosecutor decision so it's no longer treated as
      // closed/decided, and record the re-initiation for traceability.
      await editReceivingInvestigationFile3({
        id: file?.id,
        status: "assigned_to_clerk",
        prosecutor_decision_status: "pending",
        prosecutor_decision_type: "pending",
        is_re_intiated: true,
        re_initiation_date: new Date().toISOString(),
        re_initiation_reason: reInitiateReason.trim(),
      });

      await editCrimeReferences(incident?.uu_id, {
        // ...incident,
        status: "pending",
        endDate: null,
      });

      // Leave an audit trail so the re-initiation is traceable in comments.
      await addCoordinatorComment({
        investigation_file: file?.id,
        comment_text: `${t("case_reinitiated_by_coordinator", {
          defaultValue: "Case re-initiated by coordinator.",
        })} ${reInitiateReason.trim()}`,
        created_by: loginData?.full_name,
      });

      showTooltip(t("successfully_re_initiated"), "success");
      setReInitiateReason("");
      fetchData();
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail ||
          errorHandlerOnForm(error) ||
          "Failed to re-initiate",
        "error",
      );
    } finally {
      setReInitiateLoading(false);
      setShowReInitiateConfirm(false);
    }
  };

  const statusValue =
    file?.prosecutor_decision_status ??
    file?.prosecutor_decision_status ??
    file?.prosecutor_decision_status;

  const renderStatusChip = (status?: string | null) => {
    if (!status) return null;
    const s = String(status).toLowerCase();
    if (s.includes("pending"))
      return (
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
          Pending
        </span>
      );
    if (s.includes("approved"))
      return (
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
          Approved
        </span>
      );
    if (s.includes("revised"))
      return (
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300">
          Revised
        </span>
      );

    return (
      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300">
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">✖️</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Doesn't Institute Charge
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
              Review decision not to institute charges and prosecutor feedback.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 w-full">
        {/* Main Content */}
        <div className="lg:w-2/3 w-full min-w-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  Decision Information
                </h3>
                <p className="text-gray-600 text-sm mt-1 break-words">
                  Prosecutor's decision not to institute charges
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {statusValue && (
                  <div className="flex-shrink-0">
                    {renderStatusChip(statusValue)}
                  </div>
                )}
                {file?.prosecutor_decision_status === "revised" &&
                  isProsecutor && (
                    <button
                      onClick={() => setOpenEdit(true)}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0 text-sm sm:text-base"
                    >
                      {editIcon}
                      Edit Decision
                    </button>
                  )}

                {file?.prosecutor_decision_status === "approved" &&
                  isCoordinator &&
                  incident && (
                    <button
                      onClick={() => setShowReInitiateConfirm(true)}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0 text-sm sm:text-base"
                    >
                      {editIcon}
                      Re-Initiate
                    </button>
                  )}
              </div>
            </div>

            <div className="space-y-4 w-full">
              <div className="bg-gray-50 rounded-lg p-4 w-full">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Decision Reason
                </p>
                <p className="text-sm text-gray-700 break-words">
                  {file?.decision_reason ?? "-"}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <div className="bg-gray-50 rounded-lg p-4 w-full">
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    Decision Date
                  </p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {DateView(file?.decision_date) ?? "-"}
                  </p>
                </div>
                {(latestDecisionFile ||
                  file?.decision_file_info?.file_name) && (
                  <div className="bg-gray-50 rounded-lg p-4 w-full">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {t("decision_file")}
                    </p>
                    {latestDecisionFile ? (
                      <div className="space-y-2">
                        <DocumentViewerModal
                          documents={[latestDecisionFile] as any}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RECEIVING_INVESTIGATION}`}
                          layout="list"
                          hideEmptyFile={true}
                          label={
                            previousDecisionFiles.length > 0
                              ? t("latest_file")
                              : ""
                          }
                        />
                        {previousDecisionFiles.length > 0 && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={() =>
                                setShowPreviousDecisionFiles((v) => !v)
                              }
                              className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-white hover:bg-gray-100 transition-colors text-left"
                            >
                              <span className="text-xs font-medium text-gray-700">
                                {t("previous_files")} (
                                {previousDecisionFiles.length})
                              </span>
                              <svg
                                className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
                                  showPreviousDecisionFiles ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            {showPreviousDecisionFiles && (
                              <div className="px-3 py-3 border-t border-gray-200">
                                <DocumentViewerModal
                                  documents={previousDecisionFiles as any}
                                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RECEIVING_INVESTIGATION}`}
                                  layout="list"
                                  hideEmptyFile={true}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <a
                        href={file?.decision_file_info?.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors break-words max-w-full"
                      >
                        📄 {t("view")}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Coordinator Actions */}
            {isCoordinator &&
              file?.prosecutor_decision_status === "pending" && (
                <div className="mt-6 pt-6 border-t border-gray-200 w-full">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-1.5 h-6 rounded-full bg-red-600" />
                    <h4 className="text-base font-semibold text-gray-900">
                      {t("coordinator_decision", {
                        defaultValue: "Coordinator Decision",
                      })}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    {t("coordinator_decision_hint", {
                      defaultValue:
                        "Approve the prosecutor's decision, or respond back with feedback for revision.",
                    })}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        openFeedback
                          ? "bg-amber-50 border-amber-300 text-amber-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => setOpenFeedback(!openFeedback)}
                    >
                      ↩️ {t("respond_back")}
                    </button>
                    <button
                      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors ${
                        loadingApprove ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                      disabled={loadingApprove}
                      onClick={handleApprove}
                    >
                      {loadingApprove ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          {t("submitting")}
                        </>
                      ) : (
                        <>✅ {t("approve")}</>
                      )}
                    </button>
                  </div>
                </div>
              )}

            {openFeedback && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setOpenFeedback(false);
                }}
              >
                <div className="w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">
                      {t("respond_back_to_prosecutor")}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setOpenFeedback(false)}
                      className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                      aria-label={t("cancel")}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="p-6">
                    <ProsecutorFeedback
                      file={file}
                      bare
                      setRefetch={(refetch) => {
                        setOpenFeedback(false);
                        refetch && fetchData();
                      }}
                      showFeedbacks={false}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:w-1/3 w-full min-w-0">
          <ProsecutorFeedbackView file={file} />
        </div>
      </div>

      {openEdit && (
        <EditDoesntCharge
          file={file}
          onCancel={() => {
            setOpenEdit(false);
          }}
          setRefetch={fetchData}
        />
      )}

      {/* Re-Initiate Confirmation Modal */}
      {showReInitiateConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() =>
                !reInitiateLoading && setShowReInitiateConfirm(false)
              }
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              {/* Icon */}
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.928-.833-2.698 0L4.342 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Confirm Re-Initiate
              </h3>

              {/* Description */}
              <div className="mt-2">
                <p className="text-sm text-gray-500 text-center">
                  Are you sure you want to re-initiate this investigation file?
                  This action will send it back to the clerk for further
                  processing.
                </p>
              </div>

              {/* Re-initiation reason */}
              <div className="mt-4 text-left">
                <label className="text-sm font-medium text-gray-700">
                  {t("re_initiation_reason", {
                    defaultValue: "Re-initiation Reason",
                  })}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-y"
                  placeholder={t("re_initiation_reason_placeholder", {
                    defaultValue: "Why is this case being re-initiated?",
                  })}
                  value={reInitiateReason}
                  onChange={(e) => setReInitiateReason(e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-lg hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                  onClick={() => {
                    setShowReInitiateConfirm(false);
                    setReInitiateReason("");
                  }}
                  disabled={reInitiateLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`inline-flex justify-center w-full px-4 py-3 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 ${
                    reInitiateLoading || !reInitiateReason.trim()
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={handleReInitiate}
                  disabled={reInitiateLoading || !reInitiateReason.trim()}
                >
                  {reInitiateLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Re-Initiating...
                    </div>
                  ) : (
                    "Yes, Re-Initiate"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoesntChargeTab;
